import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { CalendarView } from '@/features/calendar/public';
import type { CalendarEvent } from '@/features/calendar/public';
import { useMyTimeOffRequests } from '../../hooks/useMyTimeOffRequests';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { CalendarRequestListItem } from './CalendarRequestListItem';
import { CalendarLegend } from './CalendarLegend';
import { TimeOffOverviewBar } from './TimeOffOverviewBar';
import { timeOffRequestsToCalendarEvents } from '../../adapters/time-off-request-to-calendar-events';
import type { TimeOffRequestDto } from '../../types';

interface MyCalendarViewProps {
  onSelectRequest?: (requestId: string) => void;
}

/** Fetches a large enough page to cover the visible month client-side — `GET /time-off/me/requests` has no date-range filter. */
const REQUESTS_PAGE_SIZE = 200;

export function MyCalendarView({ onSelectRequest }: MyCalendarViewProps) {
  const { t } = useTranslation('timeOff');
  const [visibleDate, setVisibleDate] = useState(() => new Date());

  const { data, isLoading, isError, refetch } = useMyTimeOffRequests({
    pageSize: REQUESTS_PAGE_SIZE,
  });
  const { data: types } = useTimeOffTypes();
  const typeNameById = useMemo(
    () =>
      new Map(
        (types ?? [])
          .filter((type): type is typeof type & { id: string } => !!type.id)
          .map((type) => [type.id, type.name ?? '']),
      ),
    [types],
  );

  const requests = useMemo<TimeOffRequestDto[]>(() => data?.items ?? [], [data]);

  const events = useMemo(
    () => timeOffRequestsToCalendarEvents(requests, typeNameById, t),
    [requests, typeNameById, t],
  );

  const sortedRequests = useMemo(
    () =>
      [...requests].sort((a, b) =>
        (b.days?.[0]?.date ?? '').localeCompare(a.days?.[0]?.date ?? ''),
      ),
    [requests],
  );

  function handleEventClick(event: CalendarEvent) {
    const requestId = event.metadata?.requestId;
    if (typeof requestId === 'string') onSelectRequest?.(requestId);
  }

  return (
    <div className="space-y-4" data-testid="time-off-calendar-view">
      <TimeOffOverviewBar requests={requests} typeNameById={typeNameById} />

      <Card>
        <CardContent className="grid gap-4 p-3 md:p-4 lg:grid-cols-[1fr_20rem] lg:gap-0 lg:divide-x lg:p-0">
          <div className="space-y-3 lg:p-4">
            <CalendarLegend />

            {isError ? (
              <div
                className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
                data-testid="time-off-calendar-error-message"
              >
                <p className="text-sm text-red-700 dark:text-red-400">{t('calendar.error')}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                  {t('common.retry')}
                </Button>
              </div>
            ) : (
              <div className="h-[560px] md:h-[640px]">
                <CalendarView
                  events={events}
                  isLoading={isLoading}
                  controlledView="month"
                  controlledDate={visibleDate}
                  onDateChange={setVisibleDate}
                  onEventClick={handleEventClick}
                  hideToolbarExtras
                  hideViewSelector
                />
              </div>
            )}
          </div>

          <div className="lg:p-4">
            <h3 className="text-sm font-semibold">{t('requests.table.title')}</h3>

            {isLoading && (
              <div
                className="mt-3 space-y-2"
                data-testid="time-off-calendar-requests-loading-skeleton"
              >
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            )}

            {!isLoading && !isError && sortedRequests.length === 0 && (
              <div data-testid="time-off-requests-table-empty-state" className="py-8 text-center">
                <p className="text-sm font-semibold">{t('requests.table.empty.title')}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('requests.table.empty.description')}
                </p>
              </div>
            )}

            {!isLoading && !isError && sortedRequests.length > 0 && (
              <div className="mt-2 divide-y">
                {sortedRequests.map((request) => (
                  <CalendarRequestListItem
                    key={request.id}
                    request={request}
                    typeName={typeNameById.get(request.typeId ?? '') ?? ''}
                    onClick={() => request.id && onSelectRequest?.(request.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
